import React, { useEffect, useState, useCallback } from 'react';
import { signing } from './api';

/**
 * Signed Documents status panel.
 *
 * Lists signature requests (newest first) via GET /api/sign/list, showing status,
 * signer, timestamps, and the copyable signing link. Optional clientId filter.
 */
const STATUS_STYLE = {
  sent:      { label: 'Sent',      bg: '#1d3a50', color: '#4a9eda' },
  viewed:    { label: 'Viewed',    bg: '#3a341d', color: '#d8b24a' },
  completed: { label: 'Completed', bg: '#1d3a2a', color: '#3fa45b' },
  declined:  { label: 'Declined',  bg: '#3a1d1d', color: '#e07070' },
  expired:   { label: 'Expired',   bg: '#2a2a2a', color: '#999' },
  error:     { label: 'Error',     bg: '#3a1d1d', color: '#e07070' },
};

export default function SignedDocumentsPanel({ darkMode, onClose, clientId = null }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const border = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const headBg = darkMode ? '#1a1a2e' : '#eef2f7';
  const rowAlt = darkMode ? '#1f1f1f' : '#fafbfc';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [checks, setChecks] = useState({});      // 5.40.3: id -> {busy, ok, text} from "Check now"

  // 5.40.3 "Check now": asks Documenso (the webhook can't reach InsureDocs); a signed request is filed through the
  // existing completion handler. Always shows a result.
  const checkNow = async (it) => {
    setChecks((c) => ({ ...c, [it.id]: { busy: true } }));
    try {
      const r = await signing.check(it.id);
      setChecks((c) => ({ ...c, [it.id]: { ok: r.data.result === 'signed', text: r.data.message || r.data.result } }));
      if (r.data.item) setItems((xs) => xs.map((x) => (x.id === it.id ? r.data.item : x)));
    } catch (e) {
      setChecks((c) => ({ ...c, [it.id]: { ok: false, text: e.response?.data?.detail || "Couldn't reach Documenso — try again in a minute" } }));
    }
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (clientId) params.client_id = clientId;
      if (statusFilter) params.status = statusFilter;
      const res = await signing.list(params);
      setItems(res.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load signatures.');
    } finally {
      setLoading(false);
    }
  }, [clientId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const copy = async (item) => {
    if (!item.signing_url) return;
    try {
      await navigator.clipboard.writeText(item.signing_url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch { /* clipboard unavailable */ }
  };

  // 5.13.0: open the original InsureDocs file a request was sent from (source_file_id),
  // through the existing logged-in /api/files/view route. The tab is opened inside the
  // click so it isn't popup-blocked, then pointed at the fetched PDF.
  const openOriginal = async (fileId) => {
    const w = window.open('', '_blank');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/files/view/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(String(res.status));
      const url = window.URL.createObjectURL(await res.blob());
      if (w) w.location.href = url; else window.open(url, '_blank');
    } catch {
      if (w) w.close();
      setError('The original file could not be opened (it may have been moved or deleted).');
    }
  };

  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: textSecondary, fontWeight: 600, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, color: textPrimary, verticalAlign: 'top' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: panelBg, color: textPrimary, border: `1px solid ${border}`, borderRadius: 10, padding: 0, width: 1000, maxWidth: '96vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>✍️ Signed Documents{clientId ? ' (this client)' : ''}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 6, background: darkMode ? '#1a1a1a' : '#fff', color: textPrimary, border: `1px solid ${border}`, fontSize: 12 }}>
              <option value="">All statuses</option>
              {['sent', 'viewed', 'completed', 'declined', 'expired', 'error'].map(s =>
                <option key={s} value={s}>{STATUS_STYLE[s]?.label || s}</option>)}
            </select>
            <button onClick={load} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', color: textSecondary, border: `1px solid ${border}`, cursor: 'pointer', fontSize: 12 }}>↻ Refresh</button>
            <span onClick={onClose} style={{ cursor: 'pointer', color: textSecondary, fontSize: 18 }}>✕</span>
          </div>
        </div>

        <div style={{ overflow: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: textSecondary, fontSize: 13 }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#e07070', fontSize: 13 }}>{error}</div>
          ) : items.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: textSecondary, fontSize: 13 }}>No signature requests yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: headBg, position: 'sticky', top: 0 }}>
                  <th style={th}>Document</th>
                  <th style={th}>Signer</th>
                  <th style={th}>Status</th>
                  <th style={th}>Sent</th>
                  <th style={th}>Signed</th>
                  <th style={th}>Link</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const ss = STATUS_STYLE[it.status] || { label: it.status, bg: '#2a2a2a', color: '#999' };
                  return (
                    <tr key={it.id} style={{ background: i % 2 ? rowAlt : 'transparent', borderTop: `1px solid ${border}` }}>
                      <td style={td}>
                        <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.document_title}>{it.document_title || '—'}</div>
                        {it.source_file_id && (
                          <button onClick={() => openOriginal(it.source_file_id)}
                            title="Open the original InsureDocs file this request was sent from"
                            style={{ marginTop: 2, padding: 0, background: 'transparent', border: 'none', color: '#4a9eda', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
                            Original
                          </button>
                        )}
                        {it.error_detail && <div style={{ fontSize: 10, color: '#e07070', marginTop: 2 }} title={it.error_detail}>⚠ {it.error_detail.slice(0, 60)}</div>}
                      </td>
                      <td style={td}>
                        <div>{it.signer_name || '—'}</div>
                        <div style={{ fontSize: 10, color: textSecondary }}>{it.signer_email}</div>
                      </td>
                      <td style={td}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: ss.bg, color: ss.color }}>{ss.label}</span>
                        {(it.status === 'sent' || it.status === 'viewed' || checks[it.id]) && (
                          <div style={{ marginTop: 4 }}>
                            {(it.status === 'sent' || it.status === 'viewed') && (
                              <button onClick={() => checkNow(it)} disabled={checks[it.id]?.busy} data-testid={`sign-check-${it.id}`}
                                style={{ padding: '2px 8px', borderRadius: 6, background: 'transparent', color: '#4a9eda', border: '1px solid #4a9eda', cursor: 'pointer', fontSize: 10, whiteSpace: 'nowrap' }}>
                                {checks[it.id]?.busy ? 'Checking…' : 'Check now'}
                              </button>
                            )}
                            {checks[it.id] && !checks[it.id].busy && (
                              <div role="status" data-testid={`sign-check-result-${it.id}`} style={{ fontSize: 10, marginTop: 2, color: checks[it.id].ok ? '#3fa45b' : textSecondary }}>
                                {checks[it.id].ok ? '✓ ' : ''}{checks[it.id].text}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, color: textSecondary, whiteSpace: 'nowrap' }}>{fmt(it.sent_at)}</td>
                      <td style={{ ...td, color: textSecondary, whiteSpace: 'nowrap' }}>{fmt(it.signed_at)}</td>
                      <td style={td}>
                        {it.signing_url ? (
                          <button onClick={() => copy(it)}
                            style={{ padding: '3px 10px', borderRadius: 6, background: copiedId === it.id ? '#3fa45b' : 'transparent', color: copiedId === it.id ? '#fff' : '#4a9eda', border: `1px solid ${copiedId === it.id ? '#3fa45b' : '#4a9eda'}`, cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {copiedId === it.id ? 'Copied!' : 'Copy link'}
                          </button>
                        ) : <span style={{ color: textSecondary, fontSize: 11 }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
